import React, { useCallback, useContext, useMemo, useState } from 'react';
import { QuantityCounterProps } from './QuantityCounter.interface';
import { SypacBox, SypacText } from '@sypac/component-library-react';
import {
	QuantityPickerContext,
	QuantityProps,
} from '../../context/QuantityPickerContext/quantity-picker.context';
import { T, useTranslate } from '@tolgee/react';
import DecreaseIcon from '../../assets/DecreaseIcon';
import IncreaseIcon from '../../assets/IncreaseIcon';
import TruckIcon from '../TruckIcon/TruckIcon';
import { toastVariant } from '../ToastVariant/toastVariant';
import { cn } from '../../lib/utils';

const QuantityCounter: React.FC<QuantityCounterProps> = (props) => {
	const { t } = useTranslate();
	const { car, payload, id } = props.counter;
	const [hoverStates, setHoverStates] = useState({
		decrease: false,
		increase: false,
	});
	const {
		totalQuantityCounter,
		inputQuantity,
		setTotalQuantityCounter,
		setInputQuantity,
	} = useContext(QuantityPickerContext);

	const totalSelectedTons =
		totalQuantityCounter?.reduce(
			(total, r) => total + r.payload * r.quantity,
			0,
		) ?? 0;

	const remainingQuantity = !props.unlimited
		? props.availableQuantity - totalSelectedTons
		: undefined;

	const isDisabledByCapacity =
		!props.unlimited &&
		typeof remainingQuantity === 'number' &&
		remainingQuantity < payload;

	const currentQuantity = useMemo(
		() => totalQuantityCounter?.find((item) => item.type === id),
		[id, totalQuantityCounter],
	);
	const [counter, setCounter] = useState(currentQuantity?.quantity || 0);
	const [payloads, setPayload] = useState(currentQuantity?.payload || 0);

	const updateQuantity = useCallback(
		(data: { payload: number; quantity: number; type: string }) => {
			const exist = totalQuantityCounter?.find((item) => item.type === id);
			let newTotals: QuantityProps[] | undefined = totalQuantityCounter?.length
				? totalQuantityCounter
				: [];
			if (exist) {
				const localTotals = totalQuantityCounter
					?.map((item) => {
						if (item.type === id) {
							return data.quantity ? data : null;
						}
						return item;
					})
					.filter((item) => !!item);
				newTotals = (localTotals as QuantityProps[]) || [];
			} else {
				newTotals = [...newTotals, data];
			}
			if (inputQuantity) {
				setInputQuantity(0);
			}
			setTotalQuantityCounter(newTotals);
		},
		[
			id,
			inputQuantity,
			setInputQuantity,
			setTotalQuantityCounter,
			totalQuantityCounter,
		],
	);

	const count = useCallback(
		(action: string) => {
			if (action === 'increase') {
				const currentTotalTons =
					totalQuantityCounter?.reduce(
						(total, r) => total + r.payload * r.quantity,
						0,
					) ?? 0;

				const nextTotalTons = currentTotalTons + payload;

				if (
					props.availableQuantity >= 0 &&
					!props.unlimited &&
					nextTotalTons > props.availableQuantity
				) {
					toastVariant(
						t(
							'quantityPicker.exceedsAllowedQuantity',
							'Exceeds the allowed contract quantity',
						),

						true,
					);
					return;
				}
				setCounter(counter + 1);
				setPayload(payload);
				updateQuantity({
					payload: payload,
					type: id,
					quantity: counter + 1,
				});
			}
			if (action === 'decrease') {
				if (counter === 0) {
					return;
				}
				setCounter(counter - 1);
				setPayload(payload);
				updateQuantity({
					payload: payload,
					type: id,
					quantity: counter - 1,
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[counter, id, payload, payloads, updateQuantity],
	);

	return (
		<div
			className={cn(
				'flex justify-between items-center transition-all',
				isDisabledByCapacity && counter === 0 && 'opacity-50',
			)}
		>
			<div className="flex gap-4 sm:gap-10">
				<div
					className={cn(
						'w-[64px] sm:w-[100px] h-[48px] xl-2xl:h-[55px] flex justify-center items-center border border-solid border-gray-10 rounded-10 transition-all box-border',
						counter > 0 ? 'shadow-md' : 'bg-light-grey',
					)}
				>
					<span className="scale-[0.8] sm:scale-[1]">
						<TruckIcon type={car} />
					</span>
				</div>

				<div className="flex flex-col gap-1">
					<SypacText variant="body-regular-large">
						<p className="text-base xl-2xl:text-xl text-gray-90">
							<T keyName={`quantityPicker.${car}`}>
								{car.charAt(0).toUpperCase() + car.slice(1)}
							</T>
						</p>
					</SypacText>
					<SypacText variant="body-regular-small">
						<p className="text-sm text-gray-40">
							<T keyName="quantityPicker.payload">Payload:</T> {payload}{' '}
							<T keyName="quantityCounter.tons">tons</T>
						</p>
					</SypacText>
				</div>
			</div>

			<SypacBox boxStyle="none" className="w-fit xl:w-[172px]" hover={false}>
				<div className="flex gap-5 xl:justify-between items-center">
					<button
						type="button"
						className="w-[28px] h-[28px] flex justify-center items-center bg-gray-10 border-none rounded-lg transition hover:bg-primary-violet cursor-pointer"
						onClick={() => count('decrease')}
						onMouseEnter={() =>
							setHoverStates({ ...hoverStates, decrease: true })
						}
						onMouseLeave={() =>
							setHoverStates({ ...hoverStates, decrease: false })
						}
					>
						<DecreaseIcon isActive={hoverStates.decrease} />
					</button>
					<SypacText variant="body-regular-medium">
						<p
							className={`mt-1 ${
								counter === 0 ? 'text-gray-40' : 'text-xl text-primary-violet'
							}`}
						>
							{counter}
						</p>
					</SypacText>
					<button
						type="button"
						className="w-[28px] h-[28px] flex justify-center items-center bg-gray-10 border-none rounded-lg transition hover:bg-primary-violet cursor-pointer"
						onClick={() => count('increase')}
						onMouseEnter={() =>
							setHoverStates({ ...hoverStates, increase: true })
						}
						onMouseLeave={() =>
							setHoverStates({ ...hoverStates, increase: false })
						}
					>
						<IncreaseIcon isActive={hoverStates.increase} />
					</button>
				</div>
			</SypacBox>
		</div>
	);
};

export default QuantityCounter;
